import * as CL from '@design-system/component-library';
import { type ReactNode, type RefObject, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { t, userInformationCopiedToAllMsg } from '../../../../common/i18n';
import { isStrictFeatureEnabled } from '../../../../common/utils/featureFlagUtils.js';
import { useFeatureFlags } from '../../../../public/site/ConfigProvider';
import { addNotification } from '../../../../public/site/notificationStore.js';
import { DuplicateContactDialog } from '../../../Dialogs/DuplicateContactDialog';
import { useDuplicateContactHandler } from '../../hooks/useDuplicateContactHandler';
import { resetMainFormOrderItemUnits } from '../../utils/placeOrderUtils';
import {
  type ContactOrPurposeHandle,
  PlaceOrderContactOrPurpose,
} from './PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose';
import type {
  ContactsUpdateMethods,
  OrderItemPurposeOrContact,
  PlaceOrderFormData,
  PlaceOrderItemUnit,
} from '../../placeOrderTypes';
import type { CharacteristicsFlagsCoCoResult } from '../../utils/characteristicsFlagsUtilsCoCo.js';
import type { UseFormReturn } from 'react-hook-form';

import './ContactOrPurposeWrapper.scss';

export interface ContactOrPurposeWrapperProps {
  showAccordion: boolean;
  defaultExpanded?: boolean;
  isMandatory: boolean;
  isCostCenterRequired: boolean;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  orderItem: PlaceOrderItemUnit;
  refId: string;
  contactsUpdateMethods: ContactsUpdateMethods;
  characteristicFlags?: CharacteristicsFlagsCoCoResult;
  accessorySharedUnitKeys?: string[];
  contentBeforePurposeOfUse?: ReactNode;
  contentBeforeCostCenter?: ReactNode;
  resetOrderItemUnits?: (methods: UseFormReturn<PlaceOrderFormData>, orderItemUnits: PlaceOrderItemUnit[]) => void;
}

export const ContactOrPurposeWrapper = ({
  showAccordion,
  defaultExpanded,
  isMandatory,
  isCostCenterRequired,
  contactFormRefs,
  orderItem,
  refId,
  contactsUpdateMethods,
  characteristicFlags,
  accessorySharedUnitKeys,
  contentBeforePurposeOfUse,
  contentBeforeCostCenter,
  resetOrderItemUnits = resetMainFormOrderItemUnits,
}: ContactOrPurposeWrapperProps) => {
  const methods = useFormContext<PlaceOrderFormData>();
  const { coco } = useFeatureFlags();
  const cocoEnabled = isStrictFeatureEnabled(coco);
  const [accordionState, setAccordionState] = useState<boolean>(defaultExpanded ?? false);

  const { dialogState, createContact, handleSelectDuplicate, handleForceCreate, handleCancel, canForceCreate } =
    useDuplicateContactHandler();

  // Watch form values for this order item to get orderItemIndex
  const allOrderItemUnits = methods.watch('products.placeOrderItemUnits');
  const orderItemIndex = allOrderItemUnits.findIndex(
    item => item.basketItemId === orderItem.basketItemId && item.basketItemIndex === orderItem.basketItemIndex
  );

  // Show copy button only for first item when there are multiple items
  const showCopyButton = orderItemIndex === 0 && allOrderItemUnits.length > 1;

  // Handle contact change from PlaceOrderContactOrPurpose - update parent form immediately
  const handleContactChange = (contactId: string | undefined) => {
    methods.setValue(`products.placeOrderItemUnits.${orderItemIndex}.userContact`, contactId);
  };

  /**
   * Helper to sync contact/purpose of use fields from form data to PlaceOrderItemUnit.
   * Maps employeeNumber (form field) to subscriptionReference (API field).
   */
  const syncContactFieldsToItem = (
    item: PlaceOrderItemUnit,
    data: OrderItemPurposeOrContact,
    contactIdOverride?: string
  ): PlaceOrderItemUnit => {
    item.purposeOfUse = data.purposeOfUse;
    item.userContact = contactIdOverride ?? data.contactId;
    item.costCenter = data.costCenter;
    item.subscriptionReference = data.employeeNumber;
    item.additionalParameters = {
      ...item.additionalParameters,
      ...data.additionalParameters,
    };
    return item;
  };

  const isTargetOfSection = (item: PlaceOrderItemUnit, data: OrderItemPurposeOrContact): boolean =>
    (item.basketItemId === data.basketItemId && item.basketItemIndex === data.basketItemIndex) ||
    (accessorySharedUnitKeys ?? []).includes(`${item.basketItemId}-${item.basketItemIndex}`);

  const handleCopyOutput = (copiedContact: OrderItemPurposeOrContact) => {
    const mainFormValues = methods.getValues();

    Object.values(contactFormRefs.current).forEach(ref => ref?.setValues(copiedContact, true));

    // Show top banner notification
    addNotification(t.GUWX(userInformationCopiedToAllMsg), 'success');

    // In CoCo, only items that actually render a contact/purpose section are synced
    const updatedValues = mainFormValues.products.placeOrderItemUnits.map(item =>
      !cocoEnabled || contactFormRefs.current[`${item.basketItemId}-${item.basketItemIndex}`]
        ? syncContactFieldsToItem(item, copiedContact)
        : item
    );

    resetOrderItemUnits(methods, updatedValues);
  };

  const updateFormWithContactId = (data: OrderItemPurposeOrContact, contactId: string) => {
    const mainFormValues = methods.getValues();
    data.contactId = contactId;
    contactFormRefs.current[`${data.basketItemId}-${data.basketItemIndex}`]?.setValues(data);

    const updatedValues = mainFormValues.products.placeOrderItemUnits.map(item => {
      if (isTargetOfSection(item, data)) {
        return syncContactFieldsToItem(item, data, contactId);
      }
      return item;
    });

    resetOrderItemUnits(methods, updatedValues);
  };

  /**
   * Handler called when "Next" is pressed.
   * Syncs the child form data to the parent form state.
   */
  const onSyncToParentForm = async (data: OrderItemPurposeOrContact): Promise<boolean> => {
    const mainFormValues = methods.getValues();
    const updatedValues = mainFormValues.products.placeOrderItemUnits.map(item => {
      if (isTargetOfSection(item, data)) {
        return syncContactFieldsToItem(item, data);
      }
      return item;
    });

    resetOrderItemUnits(methods, updatedValues);
    return true;
  };

  /**
   * Explicit user creation handler - called when user clicks "Luo uusi käyttäjä" button.
   * Creates contact with duplicate checking, then refreshes contact list.
   */
  const handleCreateUser = async (data: OrderItemPurposeOrContact): Promise<boolean> => {
    const result = await createContact(data);

    if (result.success && result.contactId) {
      // Refresh contacts list so new contact appears in dropdown
      await contactsUpdateMethods.onContactCreated?.();
      updateFormWithContactId(data, result.contactId);
      return true;
    }

    // If duplicate dialog is now open, return false (user must resolve)
    if (dialogState.isOpen) {
      return false;
    }

    // Other error occurred
    return false;
  };

  const onSelectDuplicateContact = (contactId: string) => {
    handleSelectDuplicate();
    if (dialogState.pendingData) {
      updateFormWithContactId(dialogState.pendingData, contactId);
    }
  };

  const onForceCreateContact = async () => {
    const newContactId = await handleForceCreate();
    if (newContactId && dialogState.pendingData) {
      // Refresh contacts list so new contact appears in dropdown
      await contactsUpdateMethods.onContactCreated?.();
      updateFormWithContactId(dialogState.pendingData, newContactId);
    }
  };

  const contactOrPurposeContent = (
    <PlaceOrderContactOrPurpose
      isMandatory={isMandatory}
      isCostCenterRequired={isCostCenterRequired}
      contacts={contactsUpdateMethods.contacts}
      handleCopyOutput={handleCopyOutput}
      onSyncToParentForm={onSyncToParentForm}
      handleCreateUser={handleCreateUser}
      orderItem={orderItem}
      ref={element => {
        contactFormRefs.current[refId] = element;
      }}
      setAccordionState={setAccordionState}
      onContactChange={handleContactChange}
      showCopyButton={showCopyButton}
      characteristicFlags={characteristicFlags}
      contentBeforePurposeOfUse={contentBeforePurposeOfUse}
      contentBeforeCostCenter={
        contentBeforeCostCenter && <FormProvider {...methods}>{contentBeforeCostCenter}</FormProvider>
      }
    />
  );

  if (!showAccordion) {
    return (
      <div className={cocoEnabled ? 'of-contact-or-purpose-coco' : undefined}>
        <div className="of-contact-or-purpose-wrapper">{contactOrPurposeContent}</div>
        {dialogState.isOpen && dialogState.duplicateContact && (
          <DuplicateContactDialog
            duplicateContact={dialogState.duplicateContact}
            duplicateContactType={dialogState.duplicateType}
            onCloseDialog={handleCancel}
            onSelectDuplicateContact={onSelectDuplicateContact}
            onForceCreate={canForceCreate ? onForceCreateContact : undefined}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cocoEnabled ? 'of-contact-or-purpose-coco' : undefined}>
      <CL.Accordion>
        <CL.AccordionItem
          className="of-contact-or-purpose-section"
          contentClasses="of-contact-or-purpose-content"
          defaultOpen={accordionState}
          heading={t.EMYQ('Add a user or purpose of use for the product')}
        >
          {contactOrPurposeContent}
        </CL.AccordionItem>
      </CL.Accordion>
      {dialogState.isOpen && dialogState.duplicateContact && (
        <DuplicateContactDialog
          duplicateContact={dialogState.duplicateContact}
          duplicateContactType={dialogState.duplicateType}
          onCloseDialog={handleCancel}
          onSelectDuplicateContact={onSelectDuplicateContact}
          onForceCreate={canForceCreate ? onForceCreateContact : undefined}
        />
      )}
    </div>
  );
};
